import { ProgressSpinner } from "primereact/progressspinner";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { ChartOfAccountStyles } from "../Chart-Of-Account/ChartOfAccountShared";
import CostCenterForm from "./CostCenterForm";

const emptyInputs = {
  code: "",
  name: "",
  type: "department",
  parent_id: "",
  department_id: "",
  acc_customer_id: "",
  effective_date: "",
  budget_amount: "",
  description: "",
  status: "active",
};

const today = () => {
  const date = new Date();
  const month = ("0" + (date.getMonth() + 1)).slice(-2);
  const day = ("0" + date.getDate()).slice(-2);

  return `${date.getFullYear()}-${month}-${day}`;
};

const Create = () => {
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({
    ...emptyInputs,
    effective_date: today(),
  });
  const [options, setOptions] = useState({
    parents: [],
    types: [],
    departments: [],
    customers: [],
  });
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const res = await Http.get("/cost-center-form-options");
        setOptions(res.data);
        setInputs((values) => ({
          ...values,
          code: String(res.data.next_code ?? ""),
        }));
      } catch (e) {
        console.error("Fetch error:", e);
        setError({
          load: ["Could not load the form data. Please refresh and try again."],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  // react-select hands back null when a picker is cleared, so every selection
  // falls back to "" rather than being dereferenced.
  const selectHandleChange = (setSelected, field) => (selectedOption) => {
    setSelected(selectedOption);
    setInputs((values) => ({
      ...values,
      [field]: selectedOption ? selectedOption.value : "",
    }));
  };

  const parentHandleChange = (selectedOption) => {
    setSelectedParent(selectedOption);
    setInputs((values) => ({
      ...values,
      parent_id: selectedOption ? selectedOption.value : "",
      // A child centre inherits its parent's kind; still editable afterwards.
      type:
        selectedOption && selectedOption.type
          ? selectedOption.type
          : values.type,
    }));
  };

  const validate = () => {
    const errors = {};

    if (!String(inputs.code).trim()) {
      errors.code = ["Code is required."];
    }
    if (!String(inputs.name).trim()) {
      errors.name = ["Name is required."];
    }
    if (!inputs.type) {
      errors.type = ["Type is required."];
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const formSubmit = () => {
    if (saving || !validate()) {
      return;
    }

    setSaving(true);

    Http.post("/cost-center", {
      ...inputs,
      parent_id: inputs.parent_id || null,
      department_id: inputs.department_id || null,
      acc_customer_id: inputs.acc_customer_id || null,
      effective_date: inputs.effective_date || null,
      budget_amount: inputs.budget_amount === "" ? 0 : inputs.budget_amount,
    })
      .then((res) => {
        navigate("/cost-center", {
          state: {
            message: res.data.message,
            type: res.data.type || "success",
          },
        });
      })
      .catch((e) => {
        setError(
          e.response?.data?.errors || {
            general: [
              e.response?.data?.message ||
                "Could not save the cost center. Please try again.",
            ],
          },
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">
              Create Cost Center{" "}
              {inputs.code && <span className="coa-code">{inputs.code}</span>}
            </h5>
            <p className="text-muted mb-0 tx-12">
              Track costs by department, project, branch or product
            </p>
          </div>
          <Link to="/cost-center" className="btn btn-outline-secondary">
            <i className="fa fa-arrow-left mg-r-6"></i>Back to List
          </Link>
        </div>

        <div className="am-pagebody">
          <div className="row">
            <div className="col-lg">
              <FormValidation error={error} />
            </div>
          </div>

          {loading ? (
            <div className="coa-panel">
              <div className="coa-empty">
                <ProgressSpinner
                  style={{ width: "42px", height: "42px" }}
                  strokeWidth="6"
                />
              </div>
            </div>
          ) : (
            <CostCenterForm
              inputs={inputs}
              options={options}
              selectedParent={selectedParent}
              selectedDepartment={selectedDepartment}
              selectedCustomer={selectedCustomer}
              onChange={handleChange}
              onParentChange={parentHandleChange}
              onDepartmentChange={selectHandleChange(
                setSelectedDepartment,
                "department_id",
              )}
              onCustomerChange={selectHandleChange(
                setSelectedCustomer,
                "acc_customer_id",
              )}
              onSubmit={formSubmit}
              saving={saving}
              submitLabel="Create Cost Center"
              codeHint="Next free code. You may change it."
              parentHint="Leave empty to create a top-level cost center."
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Create;
